/**
 * Student-facing landing routes, named by intent rather than by path.
 *
 * The IA splits the old triple-duty `/practice` home into four pages, so
 * "go home" and "go browse topics" are no longer the same destination. Every
 * cross-link picks one deliberately:
 *
 * - `STUDENT_HOME` — where a finished flow drops the student (recap done,
 *   homework submitted, diagnostic complete, session gone stale). Their due
 *   list, so the next thing to do is already on screen.
 * - `STUDENT_PRACTICE` — the topic browser, for *choosing* what to work on.
 *   Only for links whose intent is browsing, never for "return to home".
 * - `STUDENT_HISTORY` — the look-back page (past receipts + session log).
 *
 * Every student uses these adaptive-only destinations; no feature switch sits
 * between the intent and its route.
 */
export const STUDENT_HOME = '/tasks';
export const STUDENT_PRACTICE = '/practice';
export const STUDENT_HISTORY = '/history';

/**
 * Where a finished practice flow exits to — recap "Done", the stale-session
 * bounce, "Start fresh".
 *
 * Students land on `STUDENT_HOME`. Staff running a cohort simulation must NOT:
 * `/tasks` is USER-only (`ROUTE_ACCESS` in shared/accessPolicy.ts), so sending
 * them there bounces them off to an unrelated role default. They exit to
 * `/practice` — the surface they started the simulation from.
 *
 * Pass `useStaffLevelStreamContext().enabled`, the same
 * `practice:simulateCohorts` check that turns on the "trying out as" selector.
 */
export function practiceExitTarget(isStaffSimulation: boolean): string {
  return isStaffSimulation ? STUDENT_PRACTICE : STUDENT_HOME;
}

/**
 * Which student page opened a homework detail. Producers stamp it on the
 * navigation (`state: { from }`); the detail page's back button reads it so
 * "back" returns to the list you came from instead of always going home.
 */
export type HomeworkOrigin = 'tasks' | 'history';

export interface HomeworkBackTarget {
  /** Route the back affordances navigate to. */
  path: string;
  /**
   * Destination name for every back affordance on the homework screens
   * ("← Tasks", "Back to History"). Derived from the same origin as `path`,
   * so a label can never promise one page while the click goes to another.
   */
  label: string;
}

const HOMEWORK_ORIGIN_TARGETS: Record<HomeworkOrigin, HomeworkBackTarget> = {
  tasks: { path: STUDENT_HOME, label: 'Tasks' },
  history: { path: STUDENT_HISTORY, label: 'History' },
};

/**
 * Resolve a homework detail page's back target from `location.state.from`.
 * Unknown or missing origins (deep link, hard refresh, browser restore) fall
 * back to `STUDENT_HOME` — always a valid student destination.
 */
export function homeworkBackTarget(from: unknown): HomeworkBackTarget {
  return typeof from === 'string' && from in HOMEWORK_ORIGIN_TARGETS
    ? HOMEWORK_ORIGIN_TARGETS[from as HomeworkOrigin]
    : HOMEWORK_ORIGIN_TARGETS.tasks;
}
